import React, { useState, useEffect } from "react"
import { TenantLogo } from "@fider/components"
import { useFider } from "@fider/hooks"
import { http } from "@fider/services"

declare global {
  interface Window {
    firebase: any
  }
}

// Detect Safari/WebKit browsers that have ITP (Intelligent Tracking Prevention)
// These browsers need popup-based auth instead of redirect
const isSafariOrWebKit = (): boolean => {
  const ua = navigator.userAgent.toLowerCase()
  // Safari on macOS/iOS, or any WebKit browser on iOS
  const isSafari = ua.includes("safari") && !ua.includes("chrome") && !ua.includes("chromium")
  const isIOSWebKit = /iphone|ipad|ipod/.test(ua) && !ua.includes("crios") && !ua.includes("fxios")
  return isSafari || isIOSWebKit
}

const accountProviderConflictMessage = "An account already exists with this email. Sign in with the provider you originally used."

export const FirebaseSignInPage = () => {
  const fider = useFider()
  const firebaseProjectID = fider.settings.firebaseProjectID
  const firebaseAPIKey = fider.settings.firebaseAPIKey
  const firebaseAuthDomain = fider.settings.firebaseAuthDomain || `${firebaseProjectID}.firebaseapp.com`
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [firebaseReady, setFirebaseReady] = useState(false)
  const [processingRedirect, setProcessingRedirect] = useState(true)

  // Use popup for Safari (ITP blocks redirect-based auth)
  const usePopupAuth = isSafariOrWebKit()

  const loadScript = (src: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const script = document.createElement("script")
      script.src = src
      script.onload = () => resolve()
      script.onerror = reject
      document.head.appendChild(script)
    })
  }

  const handleSuccessRedirect = () => {
    const redirect = new URLSearchParams(window.location.search).get("redirect")
    if (redirect && redirect.startsWith("/")) {
      location.href = fider.settings.baseURL + redirect
    } else {
      location.href = fider.settings.baseURL
    }
  }

  const handleErrorFromResponse = (response: any) => {
    const data = response.data as any
    setError(data?.error || "Sign in failed")
  }

  const completeSignIn = async (user: any) => {
    try {
      setLoading(true)
      const idToken = await user.getIdToken()
      const response = await http.post("/_api/signin/sso", { idToken })
      if (response.ok) {
        handleSuccessRedirect()
      } else {
        handleErrorFromResponse(response)
        // Sign out from Firebase since backend rejected
        await window.firebase.auth().signOut()
        setLoading(false)
        setProcessingRedirect(false)
        setFirebaseReady(true)
      }
    } catch (err: any) {
      setError("Sign in failed. Please try again.")
      setLoading(false)
      setProcessingRedirect(false)
      setFirebaseReady(true)
    }
  }

  useEffect(() => {
    const initFirebase = async () => {
      try {
        if (!firebaseProjectID || !firebaseAPIKey) {
          setError("Firebase is not properly configured")
          setProcessingRedirect(false)
          return
        }

        // Load Firebase scripts
        if (!window.firebase) {
          await loadScript("https://www.gstatic.com/firebasejs/10.7.0/firebase-app-compat.js")
          await loadScript("https://www.gstatic.com/firebasejs/10.7.0/firebase-auth-compat.js")
        }

        // Initialize Firebase
        if (!window.firebase.apps?.length) {
          window.firebase.initializeApp({
            projectId: firebaseProjectID,
            apiKey: firebaseAPIKey,
            authDomain: firebaseAuthDomain,
          })
        }

        // Check for redirect result first (only valid immediately after redirect)
        let handledRedirect = false
        try {
          const result = await window.firebase.auth().getRedirectResult()
          if (result?.user && result.operationType === "signIn") {
            // This is a fresh sign-in from redirect
            handledRedirect = true
            await completeSignIn(result.user)
          }
        } catch (redirectErr: any) {
          if (redirectErr.code && redirectErr.code !== "auth/popup-closed-by-user" && redirectErr.code !== "auth/cancelled-popup-request") {
            setError(
              redirectErr.code === "auth/account-exists-with-different-credential" ? accountProviderConflictMessage : "Authentication failed. Please try again."
            )
          }
        }

        if (!handledRedirect) {
          // No redirect result - show the sign-in form
          // Only clear Firebase session if user explicitly signed out
          // (indicated by ?signout=1 URL parameter)
          const urlParams = new URLSearchParams(window.location.search)
          if (urlParams.get("signout") === "1") {
            try {
              await window.firebase.auth().signOut()
            } catch (e) {
              // Ignore signout errors
            }
            // Clean up URL by removing signout parameter
            const newUrl = window.location.pathname
            window.history.replaceState({}, "", newUrl)
          }
          setProcessingRedirect(false)
          setFirebaseReady(true)
        }
      } catch (err) {
        setError("Failed to initialize authentication")
        setProcessingRedirect(false)
      }
    }

    initFirebase()
  }, [firebaseProjectID, firebaseAPIKey, firebaseAuthDomain])

  const handleEmailSignIn = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)

    try {
      if (!window.firebase?.auth) {
        throw new Error("Firebase not initialized")
      }

      const result = await window.firebase.auth().signInWithEmailAndPassword(email, password)
      const idToken = await result.user.getIdToken()
      const response = await http.post("/_api/signin/sso", { idToken })

      if (response.ok) {
        handleSuccessRedirect()
      } else {
        handleErrorFromResponse(response)
      }
    } catch (err: any) {
      if (err.code === "auth/user-not-found" || err.code === "auth/wrong-password" || err.code === "auth/invalid-credential") {
        setError("Invalid email or password")
      } else if (err.code === "auth/invalid-email") {
        setError("Invalid email address")
      } else if (err.code === "auth/too-many-requests") {
        setError("Too many attempts. Please try again later.")
      } else {
        setError("Sign in failed. Please try again.")
      }
    } finally {
      setLoading(false)
    }
  }

  const handleGoogleSignIn = async () => {
    setError(null)
    setLoading(true)

    try {
      if (!window.firebase?.auth) {
        throw new Error("Firebase not initialized")
      }

      const provider = new window.firebase.auth.GoogleAuthProvider()

      if (usePopupAuth) {
        // Safari/WebKit: Use popup (redirect doesn't work due to ITP)
        const result = await window.firebase.auth().signInWithPopup(provider)
        await completeSignIn(result.user)
      } else {
        // Other browsers: Use redirect
        await window.firebase.auth().signInWithRedirect(provider)
      }
    } catch (err: any) {
      if (err.code === "auth/popup-closed-by-user" || err.code === "auth/cancelled-popup-request") {
        // User closed popup, just reset loading
      } else if (err.code === "auth/popup-blocked") {
        setError("Popup was blocked. Please allow popups and try again.")
      } else {
        setError("Unable to sign in with Google. Please try again.")
      }
      setLoading(false)
    }
  }

  const handleFacebookSignIn = async () => {
    setError(null)
    setLoading(true)

    try {
      if (!window.firebase?.auth) {
        throw new Error("Firebase not initialized")
      }

      const provider = new window.firebase.auth.FacebookAuthProvider()
      provider.addScope("email")

      if (usePopupAuth) {
        const result = await window.firebase.auth().signInWithPopup(provider)
        await completeSignIn(result.user)
      } else {
        await window.firebase.auth().signInWithRedirect(provider)
      }
    } catch (err: any) {
      if (err.code === "auth/popup-closed-by-user" || err.code === "auth/cancelled-popup-request") {
        // User closed popup, just reset loading
      } else if (err.code === "auth/popup-blocked") {
        setError("Popup was blocked. Please allow popups and try again.")
      } else if (err.code === "auth/account-exists-with-different-credential") {
        setError(accountProviderConflictMessage)
      } else {
        setError("Unable to sign in with Facebook. Please try again.")
      }
      setLoading(false)
    }
  }

  const handleAppleSignIn = async () => {
    setError(null)
    setLoading(true)

    try {
      if (!window.firebase?.auth) {
        throw new Error("Firebase not initialized")
      }

      const provider = new window.firebase.auth.OAuthProvider("apple.com")
      provider.addScope("email")
      provider.addScope("name")

      if (usePopupAuth) {
        // Safari/WebKit: Use popup (redirect doesn't work due to ITP)
        const result = await window.firebase.auth().signInWithPopup(provider)
        await completeSignIn(result.user)
      } else {
        // Other browsers: Use redirect
        await window.firebase.auth().signInWithRedirect(provider)
      }
    } catch (err: any) {
      if (err.code === "auth/popup-closed-by-user" || err.code === "auth/cancelled-popup-request") {
        // User closed popup, just reset loading
      } else if (err.code === "auth/popup-blocked") {
        setError("Popup was blocked. Please allow popups and try again.")
      } else {
        setError("Unable to sign in with Apple. Please try again.")
      }
      setLoading(false)
    }
  }

  if (!firebaseReady || processingRedirect) {
    return (
      <div className="firebase-signin-page">
        <style>{styles}</style>
        <div className="signin-container">
          <div className="signin-card">
            <div className="logo-container">
              <TenantLogo size={100} />
            </div>
            <div className="loading-text">{processingRedirect ? "Signing you in..." : "Loading..."}</div>
            {error && (
              <div className="error-message" role="alert" style={{ marginTop: "20px" }}>
                <svg className="error-icon" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                {error}
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="firebase-signin-page">
      <style>{styles}</style>
      <div className="signin-container">
        <div className="signin-card">
          <div className="logo-container">
            <TenantLogo size={100} />
          </div>

          <h1 className="signin-title">Sign in with your Troopsy login</h1>
          <p className="signin-subtitle">
            Your Troopsy login is the same email and password, Google account, Facebook account, or Apple ID you use in the ArmyFit app.
          </p>

          {error && (
            <div className="error-message" role="alert">
              <svg className="error-icon" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              {error}
            </div>
          )}

          {/* Social Sign In Buttons */}
          <div className="social-buttons">
            <button type="button" className="social-btn google-btn" onClick={handleGoogleSignIn} disabled={loading}>
              <svg className="social-icon" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              Continue with Google
            </button>

            <button type="button" className="social-btn facebook-btn" onClick={handleFacebookSignIn} disabled={loading}>
              <svg className="social-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07C0 18.1 4.39 23.1 10.13 24v-8.44H7.08v-3.49h3.05V9.41c0-3.03 1.79-4.7 4.53-4.7 1.31 0 2.69.24 2.69.24v2.97h-1.52c-1.49 0-1.96.93-1.96 1.89v2.26h3.33l-.53 3.49h-2.8V24C19.61 23.1 24 18.1 24 12.07z" />
              </svg>
              Continue with Facebook
            </button>

            <button type="button" className="social-btn apple-btn" onClick={handleAppleSignIn} disabled={loading}>
              <svg className="social-icon" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
              </svg>
              Continue with Apple
            </button>
          </div>

          <div className="divider">
            <span>or sign in with email</span>
          </div>

          {/* Email/Password Form */}
          <form onSubmit={handleEmailSignIn} className="signin-form">
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                disabled={loading}
              />
            </div>

            <button type="submit" className="submit-btn" disabled={loading}>
              {loading ? (
                <>
                  <span className="spinner"></span>
                  Signing in...
                </>
              ) : (
                "Sign In"
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}

// Subtle professional theme - Red, White, and Blue
const styles = `
  .firebase-signin-page {
    min-height: 100vh;
    background: linear-gradient(145deg, #1a2744 0%, #0f1724 60%, #0a0f18 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif;
    position: relative;
    overflow: hidden;
  }

  /* Subtle gradient overlay */
  .firebase-signin-page::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(ellipse at top right, rgba(40, 70, 120, 0.15) 0%, transparent 50%),
                radial-gradient(ellipse at bottom left, rgba(120, 40, 50, 0.08) 0%, transparent 50%);
    pointer-events: none;
  }

  .signin-container {
    width: 100%;
    max-width: 400px;
    position: relative;
    z-index: 1;
  }

  .signin-card {
    background: #ffffff;
    border-radius: 12px;
    padding: 40px 36px;
    box-shadow: 0 20px 40px -12px rgba(0, 0, 0, 0.4), 0 4px 20px rgba(0, 0, 0, 0.1);
  }

  .logo-container {
    display: flex;
    justify-content: center;
    margin-bottom: 20px;
  }

  .logo-container img {
    border-radius: 50%;
    border: 2px solid #e8eaed;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }

  .signin-title {
    color: #1f2937;
    font-size: 22px;
    font-weight: 650;
    line-height: 1.25;
    text-align: center;
    margin: 0 0 10px 0;
  }

  .signin-subtitle {
    color: #5f6368;
    font-size: 14px;
    line-height: 1.5;
    text-align: center;
    margin: 0 0 28px 0;
  }

  .loading-text {
    color: #5f6368;
    text-align: center;
    font-size: 15px;
  }

  .error-message {
    background: #fef2f2;
    border: 1px solid #fecaca;
    color: #b91c1c;
    padding: 12px 14px;
    border-radius: 8px;
    margin-bottom: 20px;
    font-size: 13px;
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .error-icon {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
  }

  .social-buttons {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-bottom: 20px;
  }

  .social-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    width: 100%;
    padding: 11px 16px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s ease;
    border: 1px solid transparent;
  }

  .social-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .social-icon {
    width: 18px;
    height: 18px;
  }

  .google-btn {
    background: #ffffff;
    color: #3c4043;
    border: 1px solid #dadce0;
  }

  .google-btn:hover:not(:disabled) {
    background: #f8f9fa;
    border-color: #c6c9cc;
  }

  .facebook-btn {
    background: #166fe5;
    color: #ffffff;
  }

  .facebook-btn:hover:not(:disabled) {
    background: #1464cf;
  }

  .apple-btn {
    background: #000000;
    color: #ffffff;
  }

  .apple-btn:hover:not(:disabled) {
    background: #1d1d1f;
  }

  .divider {
    display: flex;
    align-items: center;
    margin: 20px 0;
  }

  .divider::before,
  .divider::after {
    content: '';
    flex: 1;
    height: 1px;
    background: #e8eaed;
  }

  .divider span {
    padding: 0 14px;
    color: #9aa0a6;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.3px;
  }

  .signin-form {
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  .form-group {
    display: flex;
    flex-direction: column;
    gap: 5px;
  }

  .form-group label {
    color: #3c4043;
    font-size: 13px;
    font-weight: 500;
  }

  .form-group input {
    background: #ffffff;
    border: 1px solid #dadce0;
    border-radius: 8px;
    padding: 11px 14px;
    font-size: 14px;
    color: #202124;
    transition: all 0.15s ease;
  }

  .form-group input::placeholder {
    color: #9aa0a6;
  }

  .form-group input:focus {
    outline: none;
    border-color: #1a4b8c;
    box-shadow: 0 0 0 2px rgba(26, 75, 140, 0.12);
  }

  .form-group input:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .submit-btn {
    background: #8b1e32;
    color: #ffffff;
    border: none;
    border-radius: 8px;
    padding: 12px 20px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin-top: 6px;
  }

  .submit-btn:hover:not(:disabled) {
    background: #a02438;
    box-shadow: 0 2px 8px rgba(139, 30, 50, 0.25);
  }

  .submit-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .spinner {
    width: 16px;
    height: 16px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top-color: #ffffff;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  @media (max-width: 480px) {
    .signin-card {
      padding: 28px 24px;
    }
  }
`

export default FirebaseSignInPage
